import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NAME_MAX_LENGTH, type CampaignDraft } from "./campaignDraft";

export function StartOption({
  selected,
  disabled,
  title,
  onSelect,
  heading,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  title?: string;
  onSelect: () => void;
  heading: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      title={title}
      onClick={onSelect}
      className={cn(
        "flex flex-1 basis-65 items-start gap-3 rounded-xl border-[1.5px] bg-surface p-3.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        selected ? "border-primary" : "border-border hover:border-ink/40",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mt-0.5 flex size-4.5 shrink-0 items-center justify-center rounded-full border-[1.5px]",
          selected ? "border-primary" : "border-border",
        )}
      >
        {selected && <span className="size-2 rounded-full bg-primary" />}
      </span>
      <span>
        <span className="poppins block text-sm font-semibold text-ink">
          {heading}
        </span>
        <span className="block text-xs text-text-muted">{children}</span>
      </span>
    </button>
  );
}

export interface CopySourceRow {
  id: string;
  name: string;
  /** "7 placements · 14 images" */
  meta: string;
  /** "Live" / "Not live" / "Archived" */
  state: string;
}

interface NameStepProps {
  draft: CampaignDraft;
  onChange: (patch: Partial<CampaignDraft>) => void;
  copySources: CopySourceRow[];
  onChooseBlank: () => void;
  onChooseCopy: () => void;
  onPickCampaign: (id: string) => void;
}

export function NameStep({
  draft,
  onChange,
  copySources,
  onChooseBlank,
  onChooseCopy,
  onPickCampaign,
}: NameStepProps) {
  const tooLong = draft.name.trim().length > NAME_MAX_LENGTH;
  const isCopy = draft.source.kind === "copy";
  const pickedId =
    draft.source.kind === "copy" ? draft.source.campaignId : null;

  return (
    <div className="mx-auto mt-7 flex max-w-170 flex-col gap-5 rounded-2xl border border-border bg-surface p-7">
      <div>
        <div className="mb-2 text-sm font-semibold text-ink">Start from</div>
        <div
          role="radiogroup"
          aria-label="Start from"
          className="flex flex-wrap gap-3"
        >
          <StartOption
            selected={!isCopy}
            onSelect={onChooseBlank}
            heading="Blank campaign"
          >
            Pick placements and images from scratch
          </StartOption>
          <StartOption
            selected={isCopy}
            disabled={copySources.length === 0}
            title={
              copySources.length === 0
                ? "There are no campaigns to copy yet."
                : undefined
            }
            onSelect={onChooseCopy}
            heading="Copy an existing campaign"
          >
            Start with its placements, holes and images
          </StartOption>
        </div>
      </div>

      {isCopy && (
        <div className="flex flex-col gap-2">
          <div className="text-sm font-semibold text-ink">Which campaign?</div>
          <div
            role="radiogroup"
            aria-label="Which campaign?"
            className="flex max-h-80 flex-col gap-2 overflow-y-auto"
          >
            {copySources.map((source) => {
              const selected = String(pickedId) === source.id;
              return (
                <button
                  key={source.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => onPickCampaign(source.id)}
                  className={cn(
                    "flex items-center gap-3 rounded-xl border-[1.5px] bg-surface px-3.5 py-3 text-left transition-colors",
                    selected
                      ? "border-primary"
                      : "border-border hover:border-ink/40",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "flex size-4.5 shrink-0 items-center justify-center rounded-full border-[1.5px]",
                      selected ? "border-primary" : "border-border",
                    )}
                  >
                    {selected && (
                      <span className="size-2 rounded-full bg-primary" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="poppins block truncate text-sm font-semibold text-ink">
                      {source.name}
                    </span>
                    <span className="block text-xs text-text-muted">
                      {source.meta}
                    </span>
                  </span>
                  <span className="text-xs font-semibold text-text-muted">
                    {source.state}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-text-muted">
            The copy starts as a draft. It isn&rsquo;t live anywhere until you
            choose Go live. Images are shared from your library, not duplicated.
          </p>
        </div>
      )}

      <div>
        <label
          htmlFor="campaign-name"
          className="mb-1.5 block text-sm font-semibold text-ink"
        >
          Campaign name
        </label>
        <Input
          id="campaign-name"
          value={draft.name}
          aria-invalid={tooLong}
          aria-describedby="campaign-name-help"
          onChange={(e) => onChange({ name: e.target.value })}
          className="rounded-lg"
        />
        <p
          id="campaign-name-help"
          className={cn(
            "mt-1.5 text-xs",
            tooLong ? "font-semibold text-destructive" : "text-text-muted",
          )}
        >
          {tooLong
            ? `Names can be up to ${NAME_MAX_LENGTH} characters. This one is ${draft.name.trim().length}.`
            : "Tip: name it after the sponsor or season, like “Ridgeline Brewing – Winter”."}
        </p>
      </div>

      <div>
        <label
          htmlFor="campaign-description"
          className="mb-1.5 block text-sm font-semibold text-ink"
        >
          Description{" "}
          <span className="font-normal text-text-muted">
            (optional, only you see this)
          </span>
        </label>
        <Textarea
          id="campaign-description"
          rows={2}
          maxLength={1023}
          value={draft.description}
          onChange={(e) => onChange({ description: e.target.value })}
          className="rounded-lg"
        />
      </div>
    </div>
  );
}
